import { baseUrl } from "@/config/api";
import {
  ACCESS_TOKEN_STORAGE_KEY,
  getAdfsLogoutDestinationUrl,
  getSsoEntryLoginUrl,
  SSO_PROFILE_CACHE_STORAGE_KEY,
  SSO_RETURN_PATH_KEY,
} from "@/config/sso";

function ssoUrl(path: string): string {
  const root = baseUrl.replace(/\/$/, "");
  return `${root}${path.startsWith("/") ? path : `/${path}`}`;
}

export interface TokenResponse {
  access_token?: string;
  /** Filled by backend token proxy after server-side session/profile step. */
  user_email?: string;
  user_display_name?: string;
  profile?: unknown;
  [key: string]: unknown;
}

/** Read bearer token from common SSO / OAuth JSON shapes. */
export function parseAccessTokenFromPayload(payload: unknown): string | null {
  const pick = (v: unknown): string | null =>
    typeof v === "string" && v.trim().length > 0 ? v.trim() : null;

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  const o = payload as Record<string, unknown>;

  // Some APIs return the JWT as a plain string in `data` / `result`
  if (typeof o.data === "string") {
    const s = pick(o.data);
    if (s) return s;
  }
  if (typeof o.result === "string") {
    const s = pick(o.result);
    if (s) return s;
  }

  const direct =
    pick(o.access_token) ??
    pick(o.accessToken) ??
    pick(o.token) ??
    pick(o.Token) ??
    pick(o.jwt) ??
    pick(o.id_token) ??
    pick(o.bearer_token) ??
    pick(o.bearerToken);
  if (direct) return direct;

  for (const nestKey of ["data", "result", "payload", "body"]) {
    const nest = o[nestKey];
    if (nest && typeof nest === "object" && !Array.isArray(nest)) {
      const n = nest as Record<string, unknown>;
      const nested =
        pick(n.access_token) ??
        pick(n.accessToken) ??
        pick(n.token) ??
        pick(n.jwt) ??
        pick(n.id_token) ??
        pick(n.bearer_token) ??
        pick(n.bearerToken);
      if (nested) return nested;
    }
  }

  return null;
}

export async function exchangeCodeForToken(
  code: string,
  signal?: AbortSignal
): Promise<TokenResponse> {
  const trimmed = code.trim();
  if (!trimmed) {
    throw new Error("authorization_code_empty");
  }
  // Query fallback: some stacks strip POST bodies; backend accepts ?code= as well.
  const url = `${ssoUrl("/api/sso/token")}?code=${encodeURIComponent(trimmed)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code: trimmed }),
    signal,
  });
  const text = await res.text();
  let data: TokenResponse = {};
  try {
    data = text ? (JSON.parse(text) as TokenResponse) : {};
  } catch {
    if (!res.ok) {
      throw new Error(`token_exchange_failed_${res.status}`);
    }
    throw new Error("SSO token response was not valid JSON");
  }
  if (!res.ok) {
    const d = data as Record<string, unknown>;
    const errObj = d.error;
    const hint = typeof d.hint === "string" ? d.hint : "";
    const errStr =
      pick(errObj) ||
      (typeof errObj === "object" && errObj !== null
        ? pick((errObj as { message?: unknown }).message)
        : null) ||
      (typeof d.detail === "string" ? d.detail : null) ||
      pick(d.message) ||
      `token_exchange_failed_${res.status}`;
    const upstream = typeof d.upstream_url === "string" ? d.upstream_url : "";
    const ex = typeof d.exception_type === "string" ? d.exception_type : "";
    const suffix = [ex && `(${ex})`, upstream && `→ ${upstream}`].filter(Boolean).join(" ");
    const base = suffix ? `${errStr} ${suffix}`.trim() : errStr;
    throw new Error(hint ? `${base} (${hint})` : base);
  }
  return data;
}

function pick(v: unknown): string | null {
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : null;
}

function tokenStorageFingerprint(token: string): string {
  const t = token.trim();
  if (t.length < 24) return t;
  return `${t.length}:${t.slice(0, 10)}:${t.slice(-14)}`;
}

type ProfileCacheEnvelope = { fp: string; profile: Record<string, unknown> };

export function clearProfileCache(): void {
  if (!SSO_PROFILE_CACHE_STORAGE_KEY || typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(SSO_PROFILE_CACHE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function readProfileCache(token: string): Record<string, unknown> | null {
  if (!SSO_PROFILE_CACHE_STORAGE_KEY || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(SSO_PROFILE_CACHE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProfileCacheEnvelope;
    if (!parsed || typeof parsed !== "object" || typeof parsed.profile !== "object") return null;
    if (parsed.fp !== tokenStorageFingerprint(token)) return null;
    return parsed.profile;
  } catch {
    return null;
  }
}

export function writeProfileCache(token: string, profile: Record<string, unknown>): void {
  if (!SSO_PROFILE_CACHE_STORAGE_KEY || typeof localStorage === "undefined") return;
  try {
    const env: ProfileCacheEnvelope = {
      fp: tokenStorageFingerprint(token),
      profile,
    };
    localStorage.setItem(SSO_PROFILE_CACHE_STORAGE_KEY, JSON.stringify(env));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Decode JWT payload (no signature verification — IdP already issued the token). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.trim().split(".");
    if (parts.length < 2) return null;
    const seg = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = seg.length % 4 === 0 ? "" : "=".repeat(4 - (seg.length % 4));
    const json = atob(seg + pad);
    const o = JSON.parse(json) as unknown;
    return o && typeof o === "object" && !Array.isArray(o) ? (o as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Build a minimal profile object compatible with {@link getUserEmailFromProfile} / {@link getDisplayNameFromProfile}. */
function tryProfileFromAccessTokenJwt(token: string): Record<string, unknown> | null {
  const p = decodeJwtPayload(token);
  if (!p) return null;
  const email =
    pick(p.email) ??
    pick(p.upn) ??
    pick(p.preferred_username) ??
    pick((p as { unique_name?: unknown }).unique_name);
  if (!email) return null;
  const name =
    pick(p.name) ||
    `${pick(p.given_name) ?? ""} ${pick(p.family_name) ?? ""}`.trim() ||
    pick(p.preferred_username) ||
    email;
  return {
    email: email.toLowerCase(),
    name,
  };
}

export async function fetchSsoProfile(token: string): Promise<{ ok: boolean; status: number; body: unknown }> {
  const res = await fetch(ssoUrl("/api/sso/profile"), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  return { ok: res.ok, status: res.status, body };
}

/** Profile for UI: localStorage cache (same access token), else JWT claims, else one `/api/sso/profile` fetch. */
export async function getUserDetails(
  token: string | null
): Promise<Record<string, unknown> | { error: "invalid_token" } | null> {
  if (!token?.trim()) return null;

  const cached = readProfileCache(token);
  if (cached) return cached;

  const fromJwt = tryProfileFromAccessTokenJwt(token);
  if (fromJwt) {
    writeProfileCache(token, fromJwt);
    return fromJwt;
  }

  const { ok, status, body } = await fetchSsoProfile(token);
  if (status === 401) {
    clearProfileCache();
    return { error: "invalid_token" };
  }
  if (!ok || body == null) {
    return null;
  }
  if (typeof body === "object" && !Array.isArray(body) && Object.keys(body as object).length === 0) {
    return null;
  }
  if (typeof body === "object" && !Array.isArray(body)) {
    const rec = body as Record<string, unknown>;
    writeProfileCache(token, rec);
    return rec;
  }
  return null;
}

/** Primary email / UPN from SSO profile JSON (keys vary by IdP). */
export function getUserEmailFromProfile(profile: Record<string, unknown> | null | undefined): string | null {
  if (!profile || typeof profile !== "object") return null;
  const s = (v: unknown): string | null =>
    typeof v === "string" && v.trim().length > 0 ? v.trim() : null;

  const nested =
    profile.user && typeof profile.user === "object" && !Array.isArray(profile.user)
      ? (profile.user as Record<string, unknown>)
      : profile.data && typeof profile.data === "object" && !Array.isArray(profile.data)
        ? (profile.data as Record<string, unknown>)
        : null;

  const from = (o: Record<string, unknown>): string | null =>
    s(o.email) ??
    s(o.mail) ??
    s(o.userPrincipalName) ??
    s(o.upn) ??
    s(o.preferred_username);

  return from(profile) ?? (nested ? from(nested) : null);
}

/** Best-effort display name from SSO `/api/profile` JSON (keys vary by IdP). */
export function getDisplayNameFromProfile(profile: Record<string, unknown>): string {
  const s = (v: unknown): string =>
    typeof v === "string" && v.trim().length > 0 ? v.trim() : "";

  const nested =
    profile.user && typeof profile.user === "object" && !Array.isArray(profile.user)
      ? (profile.user as Record<string, unknown>)
      : profile.data && typeof profile.data === "object" && !Array.isArray(profile.data)
        ? (profile.data as Record<string, unknown>)
        : null;

  const from = (o: Record<string, unknown>): string => {
    const gn = s(o.given_name) || s(o.givenName);
    const fn = s(o.family_name) || s(o.familyName);
    if (gn && fn) return `${gn} ${fn}`;
    return (
      s(o.name) ||
      s(o.displayName) ||
      s(o.display_name) ||
      s(o.fullName) ||
      s(o.full_name) ||
      s(o.username) ||
      s(o.userName) ||
      s(o.preferred_username) ||
      s(o.email) ||
      s(o.mail) ||
      s(o.userPrincipalName) ||
      s(o.upn) ||
      (typeof o.sub === "string" ? o.sub.trim() : "")
    );
  };

  const direct = from(profile);
  if (direct) return direct;
  if (nested) {
    const inner = from(nested);
    if (inner) return inner;
  }
  return "";
}

/** True when local profile cache has an email for this token (post-login / backend-enriched token exchange). */
export function hasSsoSessionReadySync(token: string | null): boolean {
  if (!token?.trim()) return false;
  const p = readProfileCache(token);
  if (!p || typeof p !== "object") return false;
  if (Object.keys(p).length === 0) return false;
  return Boolean(getUserEmailFromProfile(p)?.trim());
}

export async function revokeSsoSession(token: string): Promise<void> {
  await fetch(ssoUrl("/api/sso/revoke"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).catch(() => undefined);
}

export function getStoredAccessToken(): string | null {
  if (!ACCESS_TOKEN_STORAGE_KEY) return null;
  return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
}

export function setStoredAccessToken(token: string): void {
  if (!ACCESS_TOKEN_STORAGE_KEY) return;
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
}

export function clearSsoClientSession(): void {
  if (!ACCESS_TOKEN_STORAGE_KEY) return;
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  clearProfileCache();
}

/** Revoke at SSO (via backend proxy), clear storage per SSO flow, then ADFS sign-out. */
export async function logout(): Promise<void> {
  const token = getStoredAccessToken();
  if (token) {
    await revokeSsoSession(token);
  }
  localStorage.clear();
  if (SSO_RETURN_PATH_KEY) {
    sessionStorage.removeItem(SSO_RETURN_PATH_KEY);
  }
  const dest = getAdfsLogoutDestinationUrl().trim();
  if (dest) {
    window.location.replace(dest);
    return;
  }
  const entry = getSsoEntryLoginUrl().trim();
  if (entry) {
    window.location.replace(entry);
    return;
  }
  window.location.replace("/");
}
